'use client';
import { ChevronLeft } from 'lucide-react';
import { useRouter } from 'next/navigation';

const STYLES =
  'w-12 h-12 mb-2 rounded-full border border-[#cdc4c1] flex items-center justify-center cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

/**
 * Is there a previous page in this tab?
 *
 * `history.length` cannot answer this — a directly opened page reports 2, not
 * 1, because the tab's initial blank document counts. The Navigation API does
 * answer it exactly. Where it is missing (Safari, older Firefox) assume there
 * is history: going back is right nearly always, and unlike navigating it can
 * never leave an extra entry behind.
 */
export function canGoBack(): boolean {
  if (typeof window === 'undefined') return true;
  const nav = (window as unknown as { navigation?: { canGoBack?: boolean } })
    .navigation;
  return typeof nav?.canGoBack === 'boolean' ? nav.canGoBack : true;
}

interface BackButtonProps {
  /**
   * Where this page's parent is, for a page that has exactly one.
   *
   * Used in two ways, neither of which adds a history entry:
   *  - as the anchor's href, so the control has a real destination to show on
   *    hover and to open in a new tab on middle-click;
   *  - as the destination when there is genuinely nothing to go back to — a
   *    bookmark, a hard refresh, a link someone pasted — where `router.back()`
   *    would do nothing at all and strand the user.
   *
   * A plain click always goes BACK. Navigating forward to the parent instead
   * would push a new entry, and then the parent's own back button returns
   * here: press, press, press and you bounce between two pages forever.
   */
  href?: string;
}

export default function BackButton({ href }: BackButtonProps) {
  const router = useRouter();

  const goBack = () => {
    if (canGoBack()) {
      router.back();
      return;
    }
    // Nothing behind us. `replace`, never `push`: this swaps the orphaned
    // entry for its parent rather than stacking another one on top.
    if (href) router.replace(href);
  };

  const icon = (
    <ChevronLeft
      className="w-6 h-6 text-[#554E49]"
      strokeWidth={3}
      aria-hidden="true"
    />
  );

  if (href) {
    return (
      <a
        href={href}
        aria-label="Go back"
        className={STYLES}
        onClick={(e) => {
          // Leave the modified clicks alone — cmd/ctrl/shift/middle-click are
          // the user asking for a new tab or window, and they get the href.
          if (
            e.defaultPrevented ||
            e.button !== 0 ||
            e.metaKey ||
            e.ctrlKey ||
            e.shiftKey ||
            e.altKey
          ) {
            return;
          }
          e.preventDefault();
          goBack();
        }}
      >
        {icon}
      </a>
    );
  }

  return (
    <button type="button" onClick={goBack} aria-label="Go back" className={STYLES}>
      {icon}
    </button>
  );
}
